import { useEffect, useMemo, useState } from 'react';
import { listInternalReferralSourcesForIntakeInternalReferralSourcesGet } from '../../services/api/sdk.gen';
import type { InternalReferralSourceResponse } from '../../services/api/types.gen';

interface UseInternalReferralSourceOptionsReturn {
  internalReferralSourceOptions: { name: string; id: string }[];
  loadingInternalReferralSources: boolean;
}

/**
 * Fetches a clinic's internal referral sources for dropdown use. The returned
 * `id` is an `internal_referral_sources.id` UUID, which is what
 * `intake.internal_referral_source_id` stores and what the push copies onto the
 * created admission.
 *
 * Reads the intake-gated route rather than the admin one, so a coordinator who
 * holds `intake:access` but no admin permission still sees the options.
 */
export function useInternalReferralSourceOptions(
  agencyClinicId: string | null | undefined
): UseInternalReferralSourceOptionsReturn {
  const [sources, setSources] = useState<InternalReferralSourceResponse[]>([]);
  const [loadingInternalReferralSources, setLoading] = useState(false);

  useEffect(() => {
    if (!agencyClinicId) {
      setSources([]);
      return;
    }

    let cancelled = false;

    const fetchSources = async () => {
      setLoading(true);
      try {
        const response =
          await listInternalReferralSourcesForIntakeInternalReferralSourcesGet({
            query: { clinic_id: agencyClinicId },
          });
        if (!cancelled && response?.data) {
          setSources(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching internal referral sources:', err);
          setSources([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSources();

    return () => {
      cancelled = true;
    };
  }, [agencyClinicId]);

  const internalReferralSourceOptions = useMemo(
    () =>
      sources
        .map(source => ({
          name: `${source.first_name} ${source.last_name}`.trim(),
          id: source.id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sources]
  );

  return { internalReferralSourceOptions, loadingInternalReferralSources };
}
